import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { 
  DollarSign, Calculator, ArrowDownCircle, ArrowUpCircle, 
  Loader2, Check, X, Clock, AlertTriangle, Banknote,
  Lock, Unlock, FileText, TrendingUp, TrendingDown, DoorOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { openCashDrawer } from '../services/PrinterService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { notifyTillChanged, fmtNZTime, fmtHours } from '../hooks/useTillStatus';

const TillRegister = () => {
  const { getAuthHeader } = useAuth();
  const { selectedStore, getCurrentStore } = useStore();
  const [loading, setLoading] = useState(true);
  const [tillStatus, setTillStatus] = useState(null);
  const [tillHistory, setTillHistory] = useState([]);
  const [cashMovements, setCashMovements] = useState([]);
  
  // Open Till Modal
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [openingFloat, setOpeningFloat] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  
  // Close Till Modal
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [countedCash, setCountedCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  
  // Denomination count for cash up
  const [denominations, setDenominations] = useState({
    notes_100: 0,
    notes_50: 0,
    notes_20: 0,
    notes_10: 0,
    notes_5: 0,
    coins_2: 0,
    coins_1: 0,
    coins_50c: 0,
    coins_20c: 0,
    coins_10c: 0
  });
  
  // Opening denominations for float verification
  const [openingDenominations, setOpeningDenominations] = useState({
    notes_100: 0,
    notes_50: 0,
    notes_20: 0,
    notes_10: 0,
    notes_5: 0,
    coins_2: 0,
    coins_1: 0,
    coins_50c: 0,
    coins_20c: 0,
    coins_10c: 0
  });
  
  const [processing, setProcessing] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  
  const fetchTillStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes, movementsRes] = await Promise.all([
        axios.get(`${API_URL}/till/status?store=${selectedStore}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/till/history?store=${selectedStore}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/cash-movements?store=${selectedStore}`, { headers: getAuthHeader() })
      ]);
      setTillStatus(statusRes.data);
      setTillHistory(historyRes.data);
      setCashMovements(movementsRes.data.slice(0, 10)); // Last 10 movements
    } catch (error) {
      console.error('Failed to fetch till status');
    } finally {
      setLoading(false);
    }
  }, [API_URL, selectedStore]); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    fetchTillStatus();
  }, [fetchTillStatus]);
  
  const calculateDenominationTotal = (denoms = denominations) => {
    return (
      denoms.notes_100 * 100 +
      denoms.notes_50 * 50 +
      denoms.notes_20 * 20 +
      denoms.notes_10 * 10 +
      denoms.notes_5 * 5 +
      denoms.coins_2 * 2 +
      denoms.coins_1 * 1 +
      denoms.coins_50c * 0.50 +
      denoms.coins_20c * 0.20 +
      denoms.coins_10c * 0.10
    );
  };
  
  const handleOpenTill = async () => {
    const floatAmount = openingFloat ? parseFloat(openingFloat) : 200;
    const denomTotal = calculateDenominationTotal(openingDenominations);
    
    // If denominations are entered, verify they match the float
    const hasDenominations = Object.values(openingDenominations).some(v => v > 0);
    if (hasDenominations && Math.abs(denomTotal - floatAmount) > 0.01) {
      toast.error(`Denominations total ($${denomTotal.toFixed(2)}) doesn't match float ($${floatAmount.toFixed(2)})`);
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/till/open`, {
        store: selectedStore,
        opening_float: floatAmount,
        denominations: hasDenominations ? openingDenominations : null,
        notes: openingNotes
      }, { headers: getAuthHeader() });
      
      toast.success(`Till opened with $${floatAmount.toFixed(2)} float`);
      setShowOpenModal(false);
      setOpeningFloat('200');
      setOpeningNotes('');
      setOpeningDenominations({
        notes_100: 0, notes_50: 0, notes_20: 0, notes_10: 0, notes_5: 0,
        coins_2: 0, coins_1: 0, coins_50c: 0, coins_20c: 0, coins_10c: 0
      });
      notifyTillChanged();
      fetchTillStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open till');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCloseTill = async () => {
    const denomTotal = calculateDenominationTotal(denominations);
    const counted = countedCash ? parseFloat(countedCash) : denomTotal;
    
    // If denominations entered, verify they match counted cash
    const hasDenominations = Object.values(denominations).some(v => v > 0);
    if (hasDenominations && countedCash && Math.abs(denomTotal - parseFloat(countedCash)) > 0.01) {
      toast.error(`Denominations total ($${denomTotal.toFixed(2)}) doesn't match counted cash ($${countedCash})`);
      return;
    }
    
    if (counted < 0) {
      toast.error('Please count your cash using the denominations below');
      return;
    }
    if (Math.abs(counted - expectedCash) > noteThreshold && closingNotes.trim().length < 5) {
      toast.error(`The count is $${Math.abs(counted - expectedCash).toFixed(2)} ${counted > expectedCash ? 'over' : 'short'} — please explain why before closing`);
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/till/close`, {
        store: selectedStore,
        counted_cash: counted,
        denominations: hasDenominations ? denominations : null,
        notes: closingNotes
      }, { headers: getAuthHeader() });
      
      const variance = response.data.variance;
      if (Math.abs(variance) < 0.01) {
        toast.success('Till closed - balanced perfectly!');
      } else if (variance > 0) {
        toast.warning(`Till closed - $${variance.toFixed(2)} OVER`);
      } else {
        toast.error(`Till closed - $${Math.abs(variance).toFixed(2)} SHORT`);
      }
      
      setShowCloseModal(false);
      setCountedCash('');
      setClosingNotes('');
      setDenominations({
        notes_100: 0, notes_50: 0, notes_20: 0, notes_10: 0, notes_5: 0,
        coins_2: 0, coins_1: 0, coins_50c: 0, coins_20c: 0, coins_10c: 0
      });
      notifyTillChanged();
      fetchTillStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close till');
    } finally {
      setProcessing(false);
    }
  };
  
  const updateDenomination = (denom, value) => {
    setDenominations({
      ...denominations,
      [denom]: parseInt(value) || 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const currentStore = getCurrentStore();
  const isOpen = tillStatus?.is_open;
  const expectedCash = tillStatus?.expected_cash || 0;
  const noteThreshold = tillStatus?.variance_note_threshold ?? 5;
  const closedToday = tillStatus?.closed_today?.[0];
  const liveCounted = parseFloat(countedCash) || calculateDenominationTotal();
  const liveVariance = liveCounted - expectedCash;
  const needsExplanation = (countedCash || calculateDenominationTotal() > 0) && Math.abs(liveVariance) > noteThreshold;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Till Register
          </h1>
          <p className="text-slate-500 mt-1">
            Manage cash drawer for {currentStore?.name || selectedStore}
            {tillStatus?.hours && <span className="text-slate-400"> · open {fmtHours(tillStatus.hours.open)}–{fmtHours(tillStatus.hours.close)}</span>}
          </p>
        </div>
        <Badge 
          className={`text-lg px-4 py-2 ${isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}
          data-testid="till-state-badge"
        >
          {isOpen ? <Unlock className="w-5 h-5 mr-2" /> : <Lock className="w-5 h-5 mr-2" />}
          Till {isOpen ? 'OPEN' : 'CLOSED'}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={openCashDrawer}
          data-testid="manual-open-drawer-btn"
          className="ml-2 border-amber-300 text-amber-900 hover:bg-amber-50"
        >
          <DoorOpen className="w-4 h-4 mr-2" /> Pop drawer
        </Button>
      </div>
      
      {/* Till Status Card */}
      <Card className={`border-2 ${tillStatus?.is_stale ? 'border-red-300 bg-red-50/40' : isOpen ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}>
        <CardContent className="pt-6">
          {tillStatus?.is_stale && (
            <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-300 bg-red-100 p-3 text-red-900" data-testid="till-stale-warning">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-bold">This till was opened on {new Date(tillStatus.opened_on + 'T00:00:00').toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' })} by {tillStatus.opened_by} and was never closed.</p>
                <p>Count the cash in the drawer now and close it (with a reason if it doesn't match), then open today's till with a fresh float.</p>
              </div>
            </div>
          )}
          {isOpen ? (
            <>
            <p className="text-xs text-slate-500 mb-3" data-testid="till-opened-by">Opened {fmtNZTime(tillStatus.opened_at)} by <span className="font-semibold text-slate-700">{tillStatus.opened_by}</span></p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                <p className="text-sm text-slate-500 mb-1">Opening Float</p>
                <p className="text-2xl font-bold text-slate-700">
                  ${tillStatus?.opening_float?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                <p className="text-sm text-slate-500 mb-1">Cash Sales Today</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="till-cash-sales">
                  +${tillStatus?.cash_sales?.toFixed(2) || '0.00'}
                </p>
                {tillStatus?.cash_refunds > 0 && <p className="text-xs text-red-600">−${tillStatus.cash_refunds.toFixed(2)} cash refunds</p>}
              </div>
              <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                <p className="text-sm text-slate-500 mb-1">Cash In/Out</p>
                <p className={`text-2xl font-bold ${(tillStatus?.net_movements || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {(tillStatus?.net_movements || 0) >= 0 ? '+' : ''}${tillStatus?.net_movements?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="text-center p-4 bg-emerald-100 rounded-sm shadow-sm">
                <p className="text-sm text-emerald-700 mb-1">Expected in Drawer</p>
                <p className="text-3xl font-bold text-emerald-800" data-testid="till-expected-cash">
                  ${expectedCash.toFixed(2)}
                </p>
              </div>
            </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Lock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-medium text-slate-600 mb-2">Till is Closed</h3>
              {closedToday ? (
                <div className="mx-auto mb-4 max-w-md rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="till-closed-today">
                  Closed today at {fmtNZTime(closedToday.closed_at)} by <span className="font-semibold">{closedToday.closed_by}</span> — counted ${Number(closedToday.counted_cash || 0).toFixed(2)} vs expected ${Number(closedToday.expected_cash || 0).toFixed(2)}
                  {Math.abs(closedToday.variance || 0) < 0.01 ? ' (balanced)' : ` (${closedToday.variance > 0 ? 'over' : 'short'} $${Math.abs(closedToday.variance).toFixed(2)})`}
                  {closedToday.closing_notes && <p className="italic mt-1">“{closedToday.closing_notes}”</p>}
                </div>
              ) : (
                <p className="text-slate-500 mb-4">Count your float and open the till to start the day</p>
              )}
              <Button 
                onClick={() => setShowOpenModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                size="lg"
                data-testid="open-till-button"
              >
                <Unlock className="w-5 h-5 mr-2" /> {closedToday ? 'Re-open Till' : 'Open Till'}
              </Button>
            </div>
          )}
          
          {isOpen && (
            <div className="flex justify-center gap-4 mt-6 pt-6 border-t">
              <Button 
                onClick={() => setShowCloseModal(true)}
                variant="outline"
                className={tillStatus?.is_stale ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : 'border-red-300 text-red-600 hover:bg-red-50'}
                data-testid="close-till-button"
              >
                <Lock className="w-4 h-4 mr-2" /> {tillStatus?.is_stale ? "Count & Close Yesterday's Till" : 'Close Till'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Cash Movements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-blue-500" />
              Today's Cash Movements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cashMovements.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No cash movements today</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cashMovements.map(movement => (
                  <div 
                    key={movement.id}
                    className={`flex items-center justify-between p-3 rounded-sm ${
                      movement.type === 'in' ? 'bg-emerald-50' : 'bg-red-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {movement.type === 'in' ? (
                        <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowUpCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{movement.reason}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(movement.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <span className={`font-bold ${movement.type === 'in' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {movement.type === 'in' ? '+' : '-'}${movement.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Till History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-500" />
              Till History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tillHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No till history yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Closed by</TableHead>
                    <TableHead className="text-right">Cash taken</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Counted</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tillHistory.map(record => (
                    <React.Fragment key={record.id}>
                    <TableRow data-testid={`till-history-${record.id}`}>
                      <TableCell className="text-sm">
                        {formatDate(record.closed_at)}
                        <span className="block text-[10px] text-slate-400">{fmtNZTime(record.opened_at)} → {fmtNZTime(record.closed_at)}</span>
                      </TableCell>
                      <TableCell className="text-sm">{record.closed_by || '—'}</TableCell>
                      <TableCell className="text-right text-emerald-700">
                        ${((record.cash_sales || 0) - (record.cash_refunds || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        ${record.expected_cash?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        ${record.counted_cash?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge 
                          variant="outline"
                          className={`${
                            Math.abs(record.variance) < 0.01 
                              ? 'bg-emerald-50 text-emerald-700' 
                              : record.variance > 0 
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {record.variance > 0 ? '+' : ''}${record.variance?.toFixed(2)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {record.closing_notes && (
                      <TableRow className="bg-slate-50/60">
                        <TableCell colSpan={6} className="py-1.5 text-xs italic text-slate-600">Reason: {record.closing_notes}</TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Open Till Modal */}
      <Dialog open={showOpenModal} onOpenChange={setShowOpenModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Unlock className="w-5 h-5 text-emerald-500" /> Open Till
            </DialogTitle>
            <DialogDescription>
              Count and verify your opening float for {currentStore?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Opening Float Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g., 200.00"
                value={openingFloat}
                onChange={(e) => setOpeningFloat(e.target.value)}
                className="text-2xl h-14 text-center font-bold"
                data-testid="opening-float"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-2">
              {[100, 150, 200, 250].map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  onClick={() => setOpeningFloat(amount.toString())}
                  className={openingFloat === amount.toString() ? 'border-emerald-500 bg-emerald-50' : ''}
                >
                  ${amount}
                </Button>
              ))}
            </div>
            
            {/* Opening Denomination Verification (Optional) */}
            <div className="space-y-3 border-t pt-4">
              <Label className="flex items-center gap-2 text-slate-600">
                <Banknote className="w-4 h-4" /> Verify Float by Denomination (Optional)
              </Label>
              
              <div className="grid grid-cols-5 gap-2">
                {[
                  { key: 'notes_100', label: '$100' },
                  { key: 'notes_50', label: '$50' },
                  { key: 'notes_20', label: '$20' },
                  { key: 'notes_10', label: '$10' },
                  { key: 'notes_5', label: '$5' },
                ].map(denom => (
                  <div key={denom.key} className="text-center">
                    <Label className="text-xs text-slate-500">{denom.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={openingDenominations[denom.key] || ''}
                      onChange={(e) => setOpeningDenominations({...openingDenominations, [denom.key]: parseInt(e.target.value) || 0})}
                      className="h-9 text-center"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { key: 'coins_2', label: '$2' },
                  { key: 'coins_1', label: '$1' },
                  { key: 'coins_50c', label: '50c' },
                  { key: 'coins_20c', label: '20c' },
                  { key: 'coins_10c', label: '10c' },
                ].map(denom => (
                  <div key={denom.key} className="text-center">
                    <Label className="text-xs text-slate-500">{denom.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={openingDenominations[denom.key] || ''}
                      onChange={(e) => setOpeningDenominations({...openingDenominations, [denom.key]: parseInt(e.target.value) || 0})}
                      className="h-9 text-center"
                    />
                  </div>
                ))}
              </div>
              {Object.values(openingDenominations).some(v => v > 0) && (
                <div className="text-right text-sm">
                  Denomination Total: <span className="font-bold">${calculateDenominationTotal(openingDenominations).toFixed(2)}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="e.g., Counted by John"
                value={openingNotes}
                onChange={(e) => setOpeningNotes(e.target.value)}
              />
            </div>
            
            <Button 
              onClick={handleOpenTill}
              disabled={processing}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
              Open Till
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Close Till Modal */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-500" /> Close Till
            </DialogTitle>
            <DialogDescription>
              Count your cash drawer and enter the total
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Expected Amount */}
            <div className="p-4 bg-slate-50 rounded-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Expected Cash in Drawer</span>
                <span className="text-2xl font-bold text-slate-800">${expectedCash.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Denomination Counter */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Banknote className="w-4 h-4" /> Cash Up - Count by Denomination
              </Label>
              
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">NOTES</p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: 'notes_100', label: '$100', color: 'bg-green-100' },
                    { key: 'notes_50', label: '$50', color: 'bg-yellow-100' },
                    { key: 'notes_20', label: '$20', color: 'bg-orange-100' },
                    { key: 'notes_10', label: '$10', color: 'bg-blue-100' },
                    { key: 'notes_5', label: '$5', color: 'bg-purple-100' },
                  ].map(denom => (
                    <div key={denom.key} className={`p-2 rounded ${denom.color}`}>
                      <Label className="text-xs font-bold text-slate-700 block text-center">{denom.label}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={denominations[denom.key] || ''}
                        onChange={(e) => setDenominations({...denominations, [denom.key]: parseInt(e.target.value) || 0})}
                        className="h-10 text-center font-bold"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">COINS</p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: 'coins_2', label: '$2', color: 'bg-amber-100' },
                    { key: 'coins_1', label: '$1', color: 'bg-amber-50' },
                    { key: 'coins_50c', label: '50c', color: 'bg-gray-100' },
                    { key: 'coins_20c', label: '20c', color: 'bg-gray-100' },
                    { key: 'coins_10c', label: '10c', color: 'bg-gray-100' },
                  ].map(denom => (
                    <div key={denom.key} className={`p-2 rounded ${denom.color}`}>
                      <Label className="text-xs font-bold text-slate-700 block text-center">{denom.label}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={denominations[denom.key] || ''}
                        onChange={(e) => setDenominations({...denominations, [denom.key]: parseInt(e.target.value) || 0})}
                        className="h-10 text-center font-bold"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-emerald-800">Denomination Total</span>
                  <span className="text-2xl font-bold text-emerald-600">${calculateDenominationTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Quick Enter Total */}
            <div className="space-y-2">
              <Label>Or Enter Total Counted Cash Manually ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter total amount"
                value={countedCash}
                onChange={(e) => setCountedCash(e.target.value)}
                className="text-xl h-12 text-center font-bold"
                data-testid="counted-cash"
              />
            </div>
            
            {/* Variance Preview */}
            {(countedCash || calculateDenominationTotal() > 0) && (
              <div className={`p-4 rounded-sm ${
                Math.abs(liveVariance) < 0.01 ? 'bg-emerald-50' : liveVariance > 0 ? 'bg-blue-50' : 'bg-red-50'
              }`} data-testid="till-variance-preview">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Variance</span>
                  <div className="flex items-center gap-2">
                    {Math.abs(liveVariance) < 0.01 ? (
                      <>
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="text-xl font-bold text-emerald-600">BALANCED</span>
                      </>
                    ) : liveVariance > 0 ? (
                      <>
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <span className="text-xl font-bold text-blue-600">+${liveVariance.toFixed(2)} OVER</span>
                      </>
                    ) : (
                      <>
                        <TrendingDown className="w-5 h-5 text-red-600" />
                        <span className="text-xl font-bold text-red-600">-${Math.abs(liveVariance).toFixed(2)} SHORT</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className={needsExplanation ? 'text-red-700 font-semibold' : ''}>
                {needsExplanation ? `Why is the count $${Math.abs(liveVariance).toFixed(2)} ${liveVariance > 0 ? 'over' : 'short'}? (required)` : `Closing Notes (optional — required if the difference is over $${noteThreshold})`}
              </Label>
              <Textarea
                placeholder={needsExplanation ? 'e.g. Gave $10 too much change to a customer at about 2pm; Paid courier $8.50 from the till without recording it' : 'e.g., Bank deposit made'}
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                rows={3}
                className={needsExplanation && closingNotes.trim().length < 5 ? 'border-red-400 focus-visible:ring-red-400' : ''}
                data-testid="closing-notes"
              />
              {needsExplanation && closingNotes.trim().length < 5 && (
                <p className="text-xs text-red-600" data-testid="closing-notes-required">The till can't be closed until the difference is explained.</p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => setShowCloseModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCloseTill}
                disabled={processing || (needsExplanation && closingNotes.trim().length < 5)}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-close-till"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Close Till
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TillRegister;
